import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, Filter, CheckSquare, Square, UserCheck, Store, X,
  AlertTriangle, ChevronRight, ShieldAlert, RefreshCw, MapPin, Clock, History,
} from "lucide-react";
import api from "@/api";
import TopBar from "@/components/TopBar";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// Stage 4E (Feb 2026): Assign Stores page
//   Left: shop list (search + district filter + assigned-only / unassigned-only chips)
//   Right: assignee picker (managers + salesmen) + Bulk Assign button
//   Manager scope: own district only (warned by backend 403)
//   Ops Admin / Super Admin: cross-district

const FILTERS = [
  { id: "all",        en: "All",        ta: "அனைத்தும்" },
  { id: "assigned",   en: "Assigned",   ta: "ஒதுக்கப்பட்டது" },
  { id: "unassigned", en: "Unassigned", ta: "ஒதுக்கப்படாதது" },
];

export default function AssignStores() {
  const { lang } = useLang();
  const { user } = useAuth();
  const nav = useNavigate();
  const isManager = user?.role === "manager";

  const [shops, setShops] = useState([]);
  const [users, setUsers] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [districtId, setDistrictId] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [assigneeId, setAssigneeId] = useState("");
  const [saving, setSaving] = useState(false);
  const [historyShop, setHistoryShop] = useState(null);  // shop whose history we're viewing

  const reload = () => {
    setLoading(true);
    Promise.all([
      api.get("/shops").then(r => r.data || []),
      api.get("/users", { params: { status: "active" } }).then(r => r.data || []),
      api.get("/districts").then(r => r.data || []),
    ]).then(([s, u, d]) => { setShops(s); setUsers(u); setDistricts(d); }).finally(() => setLoading(false));
  };
  useEffect(reload, []);

  const districtMap = useMemo(() => Object.fromEntries(districts.map(d => [d.id, d])), [districts]);

  const assignableUsers = useMemo(() =>
    users.filter(u => ["manager", "salesman"].includes(u.role) && (!isManager || u.district_id === user?.district_id))
         .sort((a, b) => (a.role === b.role ? a.name.localeCompare(b.name) : (a.role === "manager" ? -1 : 1))),
    [users, isManager, user]);

  const filteredShops = useMemo(() => {
    const q = search.trim().toLowerCase();
    return shops.filter(s => {
      if (districtId && s.district_id !== districtId) return false;
      if (statusFilter === "assigned" && !s.assigned_to_id) return false;
      if (statusFilter === "unassigned" && s.assigned_to_id) return false;
      if (q) {
        const hay = [s.name, s.name_ta, s.phone, s.address, s.owner_name, s.assigned_to_name].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [shops, search, districtId, statusFilter]);

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const selectAllVisible = () => {
    if (selected.size === filteredShops.length) setSelected(new Set());
    else setSelected(new Set(filteredShops.map(s => s.id)));
  };

  const dispName = (it) => (lang === "ta" && it?.name_ta) ? it.name_ta : (it?.name || it?.name_en || "—");

  const submitAssign = async () => {
    if (selected.size === 0) return toast.error(lang === "ta" ? "ஒரு கடை தேர்வு செய்" : "Select at least one shop");
    if (!assigneeId) return toast.error(lang === "ta" ? "ஒரு நபர் தேர்வு செய்" : "Pick an assignee");
    // Warning if any selected shop was previously assigned by Ops Admin and current actor is a Manager
    const opsAssigned = isManager && filteredShops.some(s => selected.has(s.id) && s.assigned_by_role === "ops_admin");
    if (opsAssigned) {
      const msg = lang === "ta"
        ? "சில கடைகள் முன்பு செயல்பாட்டு நிர்வாகியால் ஒதுக்கப்பட்டுள்ளன. மேலெழுதுவதா?"
        : "Some of these shops were assigned by Operations Admin. This will override the central assignment. Continue?";
      if (!window.confirm(msg)) return;
    }
    setSaving(true);
    try {
      const ids = Array.from(selected);
      const res = await api.post("/shops/bulk-assign", { shop_ids: ids, assigned_to_id: assigneeId });
      const a = res.data?.assigned || 0;
      const skipped = res.data?.skipped || 0;
      if (skipped > 0) {
        toast.warning(lang === "ta" ? `${a} ஒதுக்கப்பட்டது, ${skipped} தாண்டப்பட்டது (மாவட்டத்திற்கு வெளியே)` : `Assigned ${a}, skipped ${skipped} (out of district)`);
      } else {
        toast.success(lang === "ta" ? `${a} கடைகள் ஒதுக்கப்பட்டன` : `Assigned ${a} shops`);
      }
      setSelected(new Set());
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk assign failed");
    } finally { setSaving(false); }
  };

  const submitUnassign = async () => {
    if (selected.size === 0) return;
    const msg = lang === "ta" ? `${selected.size} கடைகளை ஒதுக்கீடிலிருந்து நீக்கவா?` : `Unassign ${selected.size} shops?`;
    if (!window.confirm(msg)) return;
    setSaving(true);
    try {
      const ids = Array.from(selected);
      await api.post("/shops/bulk-assign", { shop_ids: ids, assigned_to_id: null });
      toast.success(lang === "ta" ? "ஒதுக்கீடு நீக்கப்பட்டது" : "Unassigned");
      setSelected(new Set());
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unassign failed");
    } finally { setSaving(false); }
  };

  const stats = useMemo(() => {
    const total = shops.length;
    const assigned = shops.filter(s => s.assigned_to_id).length;
    return { total, assigned, unassigned: total - assigned };
  }, [shops]);

  return (
    <div className="min-h-screen bg-[#F5F5F7] page-slide-in pb-12">
      <TopBar title={lang === "ta" ? "கடை ஒதுக்கீடு" : "Assign Stores"} onBack={() => nav(-1)} />
      <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-4 fade-up">

        {/* HERO STATS */}
        <div className="grid grid-cols-3 gap-3" data-testid="assign-stats">
          <Stat label={lang === "ta" ? "மொத்தம்" : "Total"} value={stats.total} color="bg-[#0055FF]" />
          <Stat label={lang === "ta" ? "ஒதுக்கப்பட்டது" : "Assigned"} value={stats.assigned} color="bg-[#34C759]" />
          <Stat label={lang === "ta" ? "ஒதுக்கப்படாதது" : "Unassigned"} value={stats.unassigned} color="bg-[#FF9500]" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* LEFT: Shop list (2 cols on lg) */}
          <div className="lg:col-span-2 space-y-3">
            {/* Toolbar */}
            <div className="bg-white rounded-2xl p-3 soft-shadow flex items-center gap-2 flex-wrap" data-testid="assign-toolbar">
              <div className="flex items-center gap-2 bg-[#F5F5F7] rounded-xl px-3 py-2 flex-1 min-w-[200px]">
                <Search className="w-4 h-4 text-[#86868B]" />
                <input value={search} onChange={e => setSearch(e.target.value)} data-testid="assign-search"
                  placeholder={lang === "ta" ? "கடை தேடு…" : "Search shop…"}
                  className="bg-transparent flex-1 outline-none text-sm font-semibold" />
              </div>
              {!isManager && (
                <select value={districtId} onChange={e => setDistrictId(e.target.value)} data-testid="assign-district-filter"
                  className="bg-[#F5F5F7] rounded-xl px-3 py-2 text-sm font-semibold">
                  <option value="">{lang === "ta" ? "எல்லா மாவட்டங்களும்" : "All Districts"}</option>
                  {districts.map(d => <option key={d.id} value={d.id}>{dispName(d)}</option>)}
                </select>
              )}
              <div className="flex gap-1 bg-[#F5F5F7] rounded-xl p-1">
                {FILTERS.map(f => (
                  <button key={f.id} onClick={() => setStatusFilter(f.id)} data-testid={`assign-filter-${f.id}`}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold ${statusFilter === f.id ? "bg-white shadow text-[#0055FF]" : "text-[#86868B]"}`}>
                    {lang === "ta" ? f.ta : f.en}
                  </button>
                ))}
              </div>
              <button onClick={reload} className="p-2 rounded-xl hover:bg-gray-100" data-testid="assign-reload" title="Reload">
                <RefreshCw className="w-4 h-4 text-[#86868B]" />
              </button>
            </div>

            {/* Shop list */}
            <div className="bg-white rounded-2xl soft-shadow overflow-hidden" data-testid="assign-shop-list">
              <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between bg-gray-50/60">
                <button onClick={selectAllVisible} className="flex items-center gap-2 text-xs font-bold text-[#86868B] hover:text-[#0055FF]" data-testid="assign-select-all">
                  {selected.size > 0 && selected.size === filteredShops.length ? <CheckSquare className="w-4 h-4 text-[#0055FF]" /> : <Square className="w-4 h-4" />}
                  {selected.size > 0
                    ? `${selected.size} ${lang === "ta" ? "தேர்ந்தெடுக்கப்பட்டது" : "selected"}`
                    : (lang === "ta" ? "அனைத்தையும் தேர்வு" : "Select all")}
                </button>
                <div className="text-xs text-[#86868B]">{filteredShops.length} {lang === "ta" ? "கடைகள்" : "shops"}</div>
              </div>
              <div className="max-h-[60vh] overflow-y-auto divide-y divide-gray-100">
                {loading ? <div className="p-8 text-center text-[#86868B]">Loading…</div> :
                 filteredShops.length === 0 ? <div className="p-8 text-center text-[#86868B]" data-testid="assign-empty">{lang === "ta" ? "கடை இல்ல" : "No shops match filters"}</div> :
                 filteredShops.map(s => {
                  const isSel = selected.has(s.id);
                  const dist = districtMap[s.district_id];
                  const isOpsAssigned = s.assigned_by_role === "ops_admin";
                  const mapUrl = (s.lat && s.lng)
                    ? `https://maps.google.com/?q=${s.lat},${s.lng}`
                    : (s.address ? `https://maps.google.com/?q=${encodeURIComponent(s.address)}` : null);
                  return (
                    <div key={s.id} onClick={() => toggleOne(s.id)} data-testid={`assign-shop-${s.id}`}
                      className={`p-3 sm:p-4 flex items-start gap-3 cursor-pointer hover:bg-gray-50/60 ${isSel ? "bg-blue-50/40" : ""}`}>
                      <button className={`shrink-0 mt-1 ${isSel ? "text-[#0055FF]" : "text-[#86868B]"}`}>
                        {isSel ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        {/* Line 1: Shop name (bold + lang-aware) */}
                        <div className="heading-font font-bold text-[15px] text-[#1D1D1F] truncate">{dispName(s)}</div>
                        {/* Line 2: Address (grey, smaller) */}
                        <div className="text-xs text-[#86868B] mt-0.5 leading-snug" data-testid={`assign-shop-address-${s.id}`}>
                          {s.address ? s.address : <span className="italic opacity-70">{lang === "ta" ? "முகவரி இல்ல" : "No address"}</span>}
                        </div>
                        {/* Line 3: Phone + district + (owner if present) + W tag */}
                        <div className="text-xs text-[#86868B] mt-0.5 flex items-center gap-1.5 flex-wrap">
                          {s.phone && <span className="font-mono">{s.phone}</span>}
                          {s.owner_name && <span>· {s.owner_name}</span>}
                          {dist && <span>· {dispName(dist)}</span>}
                          {s.shop_type === "wholesaler" && <span className="pill bg-purple-50 text-purple-700">W</span>}
                          {mapUrl && (
                            <a href={mapUrl} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                              data-testid={`assign-shop-map-${s.id}`}
                              className="inline-flex items-center gap-1 text-[#34C759] hover:underline font-bold ml-auto">
                              <MapPin className="w-3 h-3" /> {lang === "ta" ? "வரைபடம்" : "Map"}
                            </a>
                          )}
                        </div>
                      </div>
                      <div className="text-right shrink-0 mt-0.5 space-y-0.5">
                        {s.assigned_to_id ? (
                          <>
                            <div className="text-xs font-bold text-[#34C759] flex items-center gap-1 justify-end">
                              <UserCheck className="w-3 h-3" /> {s.assigned_to_name}
                            </div>
                            {s.auto_assigned && (
                              <div className="text-[10px] text-[#0055FF] font-bold uppercase tracking-wider" data-testid={`assign-tag-auto-${s.id}`}>
                                {lang === "ta" ? "தானியங்கி" : "Auto"}
                              </div>
                            )}
                            {isOpsAssigned && (
                              <div className="text-[10px] text-[#5856D6] font-bold uppercase tracking-wider" data-testid={`assign-tag-ops-${s.id}`}>
                                {lang === "ta" ? "Ops Admin மூலம்" : "By Ops Admin"}
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="text-xs text-[#FF9500] font-bold uppercase tracking-wider">{lang === "ta" ? "ஒதுக்கப்படாதது" : "Unassigned"}</div>
                        )}
                        <button onClick={(e) => { e.stopPropagation(); setHistoryShop(s); }}
                          data-testid={`assign-history-${s.id}`}
                          className="text-[10px] text-[#86868B] hover:text-[#0055FF] font-bold uppercase tracking-wider flex items-center gap-1 justify-end">
                          <History className="w-3 h-3" /> {lang === "ta" ? "வரலாறு" : "History"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* RIGHT: Assignee picker */}
          <div className="space-y-3">
            <div className="bg-white rounded-2xl p-5 soft-shadow space-y-3 sticky top-4" data-testid="assign-picker">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold">{lang === "ta" ? "ஒதுக்கு" : "Assign to"}</div>
                <h3 className="heading-font font-bold text-xl">{lang === "ta" ? "நபரை தேர்வு செய்" : "Pick assignee"}</h3>
              </div>

              <div className="max-h-[40vh] overflow-y-auto space-y-1.5">
                {assignableUsers.map(u => {
                  const dist = districtMap[u.district_id];
                  const active = assigneeId === u.id;
                  return (
                    <button key={u.id} onClick={() => setAssigneeId(u.id)} data-testid={`assign-assignee-${u.id}`}
                      className={`w-full text-left p-3 rounded-xl flex items-center gap-3 transition ${active ? "bg-[#0055FF] text-white" : "hover:bg-gray-50 text-[#1D1D1F]"}`}>
                      <div className={`w-8 h-8 rounded-full grid place-items-center text-xs font-bold ${active ? "bg-white/20" : (u.role === "manager" ? "bg-[#5856D6] text-white" : "bg-[#FF9500] text-white")}`}>
                        {(u.name || "?")[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold truncate">{(lang === "ta" && u.name_ta) ? u.name_ta : u.name}</div>
                        <div className={`text-xs truncate ${active ? "opacity-90" : "text-[#86868B]"}`}>
                          {u.role === "manager" ? (lang === "ta" ? "மேனேஜர்" : "Manager") : (lang === "ta" ? "சேல்ஸ்மேன்" : "Salesman")}
                          {dist && ` · ${dispName(dist)}`}
                        </div>
                      </div>
                      {active && <ChevronRight className="w-4 h-4" />}
                    </button>
                  );
                })}
                {assignableUsers.length === 0 && <div className="text-center text-[#86868B] py-6 text-sm">{lang === "ta" ? "நபர் இல்ல" : "No one to assign"}</div>}
              </div>

              {/* Selected count + actions */}
              <div className="border-t border-gray-100 pt-3 space-y-2">
                <div className="text-xs text-[#86868B]" data-testid="assign-selection-count">
                  {selected.size} {lang === "ta" ? "தேர்வு" : "selected"}
                </div>
                <button onClick={submitAssign} disabled={saving || selected.size === 0 || !assigneeId} data-testid="assign-submit-btn"
                  className="w-full bg-[#0055FF] hover:bg-[#0044CC] disabled:opacity-40 text-white rounded-xl py-3 font-bold flex items-center justify-center gap-2">
                  <UserCheck className="w-4 h-4" /> {saving ? "…" : (lang === "ta" ? "ஒதுக்கு" : "Assign")}
                </button>
                <button onClick={submitUnassign} disabled={saving || selected.size === 0} data-testid="assign-unassign-btn"
                  className="w-full bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-40 text-[#FF3B30] rounded-xl py-2.5 font-bold flex items-center justify-center gap-1.5 text-sm">
                  <X className="w-4 h-4" /> {lang === "ta" ? "ஒதுக்கீடு நீக்கு" : "Unassign"}
                </button>
              </div>
            </div>

            {isManager && (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 text-xs text-amber-900 flex gap-2" data-testid="assign-manager-warning">
                <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
                {lang === "ta"
                  ? "உங்கள் மாவட்டத்திற்கு உள்ளே மட்டும் ஒதுக்க முடியும். Ops Admin ஒதுக்கீடு மேலெழுதினால் எச்சரிக்கை வரும்."
                  : "You can only assign within your own district. Overriding an Ops Admin assignment will warn you first."}
              </div>
            )}
          </div>
        </div>
      </div>

      {historyShop && <HistoryDrawer shop={historyShop} lang={lang} onClose={() => setHistoryShop(null)} />}
    </div>
  );
}

const HistoryDrawer = ({ shop, lang, onClose }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/shops/${shop.id}/assignments`).then(r => setData(r.data)).catch(() => setData({ history: [] }));
  }, [shop.id]);
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 grid place-items-center p-4" onClick={onClose} data-testid="assign-history-drawer">
      <div className="bg-white rounded-3xl max-w-xl w-full max-h-[85vh] overflow-y-auto soft-shadow-md fade-up" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white p-5 border-b border-gray-100 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold">{lang === "ta" ? "ஒதுக்கீடு வரலாறு" : "Assignment History"}</div>
            <h3 className="heading-font font-bold text-xl">{(lang === "ta" && shop.name_ta) ? shop.name_ta : shop.name}</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100" data-testid="assign-history-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          {!data ? <div className="text-center text-[#86868B] py-6">Loading…</div> :
           (data.history || []).length === 0 ? <div className="text-center text-[#86868B] py-6">{lang === "ta" ? "வரலாறு இல்ல" : "No history yet"}</div> :
           (data.history || []).map((h, i) => (
            <div key={i} className="flex gap-3 p-3 rounded-xl bg-[#F5F5F7]" data-testid={`assign-history-entry-${i}`}>
              <div className="w-8 h-8 rounded-full bg-white grid place-items-center shrink-0"><Clock className="w-4 h-4 text-[#0055FF]" /></div>
              <div className="flex-1 min-w-0 text-sm">
                <div className="font-bold text-[#1D1D1F]">
                  {h.previous_assignee_name ? (
                    <>{h.previous_assignee_name} → {h.new_assignee_name || (lang === "ta" ? "ஒதுக்கப்படாதது" : "Unassigned")}</>
                  ) : h.new_assignee_name ? (
                    <>{lang === "ta" ? "புதிதாக ஒதுக்கப்பட்டது:" : "Assigned to:"} {h.new_assignee_name}</>
                  ) : (
                    <>{lang === "ta" ? "ஒதுக்கீடு நீக்கப்பட்டது" : "Unassigned"}</>
                  )}
                </div>
                <div className="text-xs text-[#86868B] mt-0.5">
                  {lang === "ta" ? "ஆல்" : "By"}: <b>{h.by_name}</b> ({h.by_role}){h.auto && <span className="text-[#0055FF] ml-1 font-bold">· {lang === "ta" ? "தானியங்கி" : "AUTO"}</span>}
                </div>
                <div className="text-[10px] text-[#86868B] font-mono mt-0.5">{(h.at || "").replace("T", " ").slice(0, 19)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, value, color }) => (
  <div className="bg-white rounded-2xl p-4 soft-shadow flex items-center gap-3">
    <div className={`w-10 h-10 rounded-xl ${color} grid place-items-center text-white`}>
      <Store className="w-5 h-5" />
    </div>
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold">{label}</div>
      <div className="heading-font text-2xl font-bold text-[#1D1D1F]">{value}</div>
    </div>
  </div>
);
